import { parse, type LetterparserNode } from 'letterparser';

import recoverBoundarylessMultipart from './candidateBoundaryRecovery';
import { hasMimeHeaderBlock, MIME_HEADER_FIELD } from './candidateMimeHeaders';

export interface CandidateByteInput {
    rawUtf8: string;
    raw: string;
    node?: LetterparserNode;
    warnings: string[];
    warning?: string;
}

const BINARY_CHUNK_BYTES = 8192;
const UNSUPPORTED_ENCODING = 'This email uses an unsupported text encoding and could not be decoded safely.';
const UNSUPPORTED_PART = 'An email part uses an unsupported encoding and could not be decoded safely.';
const WINDOWS_1252_C1_START = 0x80;
const WINDOWS_1252_C1_INDEX = '\u20ac\u0081\u201a\u0192\u201e\u2026\u2020\u2021'
    + '\u02c6\u2030\u0160\u2039\u0152\u008d\u017d\u008f'
    + '\u0090\u2018\u2019\u201c\u201d\u2022\u2013\u2014'
    + '\u02dc\u2122\u0161\u203a\u0153\u009d\u017e\u0178';

const normalizeWindows1252 = (decoded: string, encoding: string): string => (
    encoding === 'windows-1252' ? decoded.replace(/[\u0080-\u009f]/g, (character) => (
        WINDOWS_1252_C1_INDEX[character.charCodeAt(0) - WINDOWS_1252_C1_START]
    )) : decoded
);

const binaryString = (bytes: Uint8Array): string => {
    const chunks: string[] = [];
    for (let index = 0; index < bytes.length; index += BINARY_CHUNK_BYTES) {
        chunks.push(String.fromCharCode(...bytes.subarray(index, index + BINARY_CHUNK_BYTES)));
    }
    return chunks.join('');
};
const bytesOf = (binary: string): Uint8Array => Uint8Array.from(binary, (character) => character.charCodeAt(0));

const children = (node: LetterparserNode): LetterparserNode[] => {
    if (typeof node.body === 'string' || node.body instanceof Uint8Array) return [];
    return Array.isArray(node.body) ? node.body : [node.body];
};

const normalizeUtf8Headers = (binary: string, tree: LetterparserNode): string => {
    const boundaries = new Set<string>();
    const collect = (node: LetterparserNode): void => {
        if (node.contentType.type.startsWith('multipart/') && node.contentType.parameters.boundary) {
            boundaries.add(node.contentType.parameters.boundary);
        }
        children(node).forEach(collect);
    };
    collect(tree);
    let inHeaders = true;
    let headerName = '';
    let contentType = '';
    return binary.split(/\r?\n/).map((line) => {
        const boundaryLine = line.trimEnd();
        if ([...boundaries].some((boundary) => boundaryLine === `--${boundary}`)) {
            inHeaders = true;
            headerName = '';
            contentType = '';
            return line;
        }
        if ([...boundaries].some((boundary) => boundaryLine === `--${boundary}--`)) {
            inHeaders = false;
            return line;
        }
        if (!inHeaders) return line;
        if (line === '') {
            inHeaders = /^message\/(?:rfc822|global)(?:;|$)/i.test(contentType);
            headerName = '';
            contentType = '';
            return line;
        }
        const decoded = new TextDecoder('utf-8', { fatal: true }).decode(bytesOf(line));
        const header = decoded.match(MIME_HEADER_FIELD);
        if (header) {
            headerName = header[1].toLowerCase();
            if (headerName === 'content-type') {
                const [, , value] = header;
                contentType = value.trim();
            }
        } else if (/^[ \t]/.test(decoded) && headerName === 'content-type') {
            contentType += decoded.trim();
        }
        return decoded;
    }).join('\n');
};

export const parseCandidateBytes = (bytes: Uint8Array): CandidateByteInput => {
    const rawUtf8 = new TextDecoder('utf-8').decode(bytes);
    const warnings: string[] = [];
    const result = (raw: string, node?: LetterparserNode): CandidateByteInput => ({
        rawUtf8, raw, node, warnings, warning: warnings.length ? warnings.join(' ') : undefined,
    });
    const originalBinary = binaryString(bytes);
    const binary = hasMimeHeaderBlock(originalBinary)
        ? recoverBoundarylessMultipart(originalBinary) ?? originalBinary
        : originalBinary;
    if (!hasMimeHeaderBlock(binary)) {
        try { return result(new TextDecoder('utf-8', { fatal: true }).decode(bytes)); } catch {
            warnings.push('Headerless text has no supported declared charset; decoding was not guessed.');
            return result(UNSUPPORTED_ENCODING);
        }
    }
    let tree: LetterparserNode;
    try {
        tree = parse(binary);
    } catch (error) {
        const encodingError = /\b(?:encoding|charset)\b.*\b(?:not supported|invalid|unknown)\b/i;
        if (error instanceof Error && encodingError.test(error.message)) {
            warnings.push('An encoded MIME part uses an unsupported charset and could not be decoded safely.');
        }
        try { return result(new TextDecoder('utf-8', { fatal: true }).decode(bytes)); } catch {
            warnings.push('MIME parsing failed before a safe text charset could be determined.');
            return result(UNSUPPORTED_ENCODING);
        }
    }
    try {
        const normalized = normalizeUtf8Headers(binary, tree);
        tree = parse(normalized);
    } catch {
        warnings.push('A raw MIME header is not valid UTF-8; its encoding was not guessed.');
        return result(UNSUPPORTED_ENCODING);
    }
    const decodeLeaves = (node: LetterparserNode): LetterparserNode => {
        const transfer = (node.headers['Content-Transfer-Encoding'] || '').trim().toLowerCase();
        if (transfer === 'quoted-printable' && !node.contentType.type.startsWith('text/')) {
            warnings.push('A quoted-printable binary attachment is unavailable; '
                + 'the MIME decoder cannot guarantee exact octet fidelity.');
            return { ...node, headers: { ...node.headers, 'Content-Id': undefined }, body: new Uint8Array() };
        }
        if (typeof node.body !== 'string') {
            if (node.body instanceof Uint8Array) return node;
            return {
                ...node,
                body: Array.isArray(node.body) ? node.body.map(decodeLeaves) : decodeLeaves(node.body),
            };
        }
        if (transfer === 'base64' || transfer === 'quoted-printable') {
            if (node.contentType.type.startsWith('text/') && node.body.includes('\ufffd')) {
                warnings.push('Decoded email text contains replacement characters; '
                    + 'this may indicate source or encoding damage.');
            }
            return node;
        }
        if (!node.contentType.type.startsWith('text/')) {
            warnings.push('A raw binary attachment is unavailable because exact bytes could not be recovered safely.');
            return { ...node, headers: { ...node.headers, 'Content-Id': undefined }, body: new Uint8Array() };
        }
        try {
            if (!['', '7bit', '8bit', 'binary'].includes(transfer)) throw new Error('Unknown transfer encoding.');
            const decoder = new TextDecoder(node.contentType.encoding || 'utf-8', { fatal: true });
            if (/^utf-16/.test(decoder.encoding)) {
                throw new Error('Raw UTF-16 MIME cannot survive the parser line-boundary normalization.');
            }
            return { ...node, body: normalizeWindows1252(decoder.decode(bytesOf(node.body)), decoder.encoding) };
        } catch {
            warnings.push('A raw text part has an unknown, unsupported or invalid declared charset.');
            return {
                ...node,
                body: UNSUPPORTED_PART,
                contentType: { type: 'text/plain', encoding: 'utf-8', parameters: { charset: 'utf-8' } },
            };
        }
    };
    return result(rawUtf8, decodeLeaves(tree));
};
